import {
    Component,
    ContentChild,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { CerrixSlideOutComponent } from "../cerrix-slide-out/cerrix-slide-out.component";
import { CommonModule } from "@angular/common";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { CerrixSlideOutEditItemMenuItems } from "./models/cerrix-slide-out-edit-item-menu-items.model";
import {
    CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
    CerrixSlideOutEditItemSection,
} from "./models/cerrix-slide-out-edit-item-section.model";
import { CerrixConfirmDialogComponent } from "../cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { CerrixDialogService } from "../cerrix-dialog/cerrix-dialog-service.component";

@Component({
    selector: "app-cerrix-slide-out-edit-item",
    templateUrl: "./cerrix-slide-out-edit-item.component.html",
    styleUrls: ["./cerrix-slide-out-edit-item.component.scss"],
    imports: [
        CommonModule,
        CerrixSlideOutComponent,
        CerrixConfirmDialogComponent,
        TranslatePipe,
    ],
    viewProviders: [
        provideTranslationScope(scope),
    ],
    encapsulation: ViewEncapsulation.None,
    host: {
        "[class.open]": "isOpen",
    },
})
export class CerrixSlideOutEditItemComponent {
    @Input() isOpen: boolean = false;
    @Input() items: CerrixSlideOutEditItemMenuItems;
    @Input() title: string;
    @Output() isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() itemsChange: EventEmitter<CerrixSlideOutEditItemMenuItems> =
        new EventEmitter<CerrixSlideOutEditItemMenuItems>();
    @Output() sectionChanged: EventEmitter<void> = new EventEmitter<void>();

    @ContentChild(CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION)
    private section: CerrixSlideOutEditItemSection;

    protected readonly unsavedChangesDialogName = "slideOutEditItemUnsavedChangesDialog";

    private _dialogService = inject(CerrixDialogService);
    private pendingLeave: () => void;

    /** Closes the slide-out, asking first when the active section has unsaved changes. */
    public requestClose(): void {
        this.leaveSection(() => this.close());
    }

    protected openSection(section: string): void {
        if (section === this.items.activeItem) {
            return;
        }
        this.leaveSection(() => {
            this.itemsChange.emit({ ...this.items, activeItem: section });
            this.sectionChanged.emit();
        });
    }

    // Leaving destroys the section component, so discarding needs nothing beyond leaving.
    protected discardAndLeave(): void {
        this._dialogService.closeDialog(this.unsavedChangesDialogName);
        this.pendingLeave();
    }

    protected async saveAndLeave(): Promise<void> {
        this._dialogService.closeDialog(this.unsavedChangesDialogName);
        if (await this.section.save()) {
            this.pendingLeave();
        }
    }

    private leaveSection(leave: () => void): void {
        if (!this.section?.hasUnsavedChanges()) {
            leave();
            return;
        }
        this.pendingLeave = leave;
        this._dialogService.openDialog(this.unsavedChangesDialogName);
    }

    private close(): void {
        if (!this.items?.items?.length) {
            this.isOpenChange.emit(false);
            return;
        }
        this.itemsChange.emit({ ...this.items, activeItem: this.items.items[0].id });
        this.isOpenChange.emit(false);
    }
}
